// File: e1c/src/components/content_editor/ScaleBlock.js
// REFACTORED: This version is a fully controlled component, removing internal state
// to resolve input responsiveness issues. It now relies entirely on props from its parent.

import React, { useCallback, useMemo } from 'react';
import { Paper, TextField, Slider, IconButton, Box, Grid, Typography } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

const ScaleBlock = React.memo(({ id, question, options = {}, onChange, onDelete }) => {

    const memoizedOptions = useMemo(() => ({
        min: options.min ?? 0,
        max: options.max ?? 10,
        step: options.step ?? 1,
        minLabel: options.minLabel ?? 'Low',
        maxLabel: options.maxLabel ?? 'High',
    }), [options]);


    const handleQuestionChange = useCallback((event) => {
        if (onChange) { onChange({ question: event.target.value }); }
    }, [onChange]);

    const handleOptionChange = useCallback((optionName, value) => {
        if (onChange) {
            onChange({ options: { [optionName]: value } });
        }
    }, [onChange]);
    
    const handleDeleteClick = useCallback(() => {
        if (onDelete) { onDelete(id); }
    }, [id, onDelete]);

    const previewMin = Number.isFinite(memoizedOptions.min) ? memoizedOptions.min : 0;
    const previewMax = Number.isFinite(memoizedOptions.max) && memoizedOptions.max > previewMin ? memoizedOptions.max : previewMin + 10;
    const previewStep = Number.isFinite(memoizedOptions.step) && memoizedOptions.step > 0 ? memoizedOptions.step : 1;

    return (
        <Paper variant="outlined" sx={{ p: 2, mb: 2, position: 'relative', '&:hover .delete-button': { opacity: 1 } }}>
            <IconButton
                className="delete-button" aria-label="delete block" onClick={handleDeleteClick} size="small"
                sx={{ position: 'absolute', top: 8, right: 8, color: 'grey.500', opacity: 0.5, transition: 'opacity 0.2s', '&:hover': { opacity: 1, color: 'error.main' } }}
            >
                <DeleteOutlineIcon fontSize="small"/>
            </IconButton>
            <TextField
                fullWidth variant="outlined" size="small" label={`Scale Question Text (ID: ${id})`} multiline
                value={question || ''} onChange={handleQuestionChange}
                placeholder="Enter the question text for the scale..." sx={{ mb: 1 }}
            />
            
            
            <Box sx={{ mt: 2, p:1, border: '1px dashed lightgrey', borderRadius: 1 }}>
                <Typography variant="caption" display="block" color="text.secondary" sx={{mb:1}}>
                    Patient Preview:
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', px: 1 }}>
                    <Typography variant="body2" sx={{ minWidth: '50px', textAlign: 'center', mr: 1 }}>{memoizedOptions.minLabel || `(${previewMin})`}</Typography>
                    <Slider
                        value={previewMin} min={previewMin} max={previewMax} step={previewStep}
                        marks={[{ value: previewMin, label: '' }, { value: previewMax, label: '' }]}
                        valueLabelDisplay="off" disabled sx={{ mx: 1 }}
                    />
                    <Typography variant="body2" sx={{ minWidth: '50px', textAlign: 'center', ml: 1 }}>{memoizedOptions.maxLabel || `(${previewMax})`}</Typography>
                </Box>
            </Box>


            <Grid container spacing={1.5}>
                 <Grid item xs={6} sm={3} md={2}>
                    <TextField type="number" label="Min Value" size="small" value={memoizedOptions.min ?? ''} 
                               onChange={(e) => handleOptionChange('min', e.target.value === '' ? undefined : parseInt(e.target.value, 10))} fullWidth />
                 </Grid>
                 <Grid item xs={6} sm={3} md={2}>
                    <TextField type="number" label="Max Value" size="small" value={memoizedOptions.max ?? ''} 
                               onChange={(e) => handleOptionChange('max', e.target.value === '' ? undefined : parseInt(e.target.value, 10))} fullWidth />
                 </Grid>
                 <Grid item xs={6} sm={3} md={2}>
                    <TextField type="number" label="Step" size="small" value={memoizedOptions.step ?? ''} 
                               onChange={(e) => handleOptionChange('step', e.target.value === '' ? 1 : parseFloat(e.target.value))} fullWidth inputProps={{ step: "any" }}/>
                 </Grid>
                 <Grid item xs={6} sm={3} md={3}>
                    <TextField label="Min Label" size="small" value={memoizedOptions.minLabel} 
                               onChange={(e) => handleOptionChange('minLabel', e.target.value)} fullWidth placeholder="e.g., Low"/>
                 </Grid>
                 <Grid item xs={12} sm={12} md={3}>
                    <TextField label="Max Label" size="small" value={memoizedOptions.maxLabel} 
                               onChange={(e) => handleOptionChange('maxLabel', e.target.value)} fullWidth placeholder="e.g., High"/>
                 </Grid>
            </Grid>
        </Paper>
    );
});

export default ScaleBlock;